import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, Calendar, RefreshCw, Search, Copy, Check, Pause, Play, ShieldAlert, LogIn, Trash2 } from "lucide-react";

function timeAgo(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const ms = Date.now() - d.getTime();
        const sec = Math.floor(ms / 1000);
        if (sec < 60) return "just now";
        const min = Math.floor(sec / 60);
        if (min < 60) return `${min} min ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
        const day = Math.floor(hr / 24);
        if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
        const mo = Math.floor(day / 30);
        return `${mo} month${mo === 1 ? "" : "s"} ago`;
    } catch {
        return iso;
    }
}

export default function Companies() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [copiedId, setCopiedId] = useState("");
    const [impersonating, setImpersonating] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/companies");
            setItems(data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load companies");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = items.filter((c) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (c.company_id || "").toLowerCase().includes(q) ||
            (c.name || "").toLowerCase().includes(q) ||
            (c.address || "").toLowerCase().includes(q)
        );
    });

    const totalUsers = items.reduce((s, c) => s + (c.user_count || 0), 0);

    const copyId = async (id) => {
        try {
            await navigator.clipboard.writeText(id);
            setCopiedId(id);
            setTimeout(() => setCopiedId(""), 1500);
        } catch {}
    };

    const onToggleStatus = async (company) => {        const isActive = (company.status || "active") === "active";
        const verb = isActive ? "Suspend" : "Reactivate";
        const newStatus = isActive ? "suspended" : "active";
        let reason = "";
        if (isActive) {
            reason = window.prompt(`${verb} ${company.name || company.company_id}?\n\nReason (will be stored on the record):`) || "";
            if (reason === null) return;
        } else {
            if (!window.confirm(`${verb} ${company.name || company.company_id}? Their admin will be able to log in again.`)) return;
        }
        try {
            await api.put(`/companies/${company.company_id}/status`, { status: newStatus, reason });
            toast.success(`${company.company_id}${isActive ? " suspended" : " reactivated"}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Status update failed");
        }
    };

    const onImpersonate = async (company) => {
        const cname = company.name || company.company_id;
        if (!window.confirm(
            `Impersonate ${cname}?\n\nYou'll be signed into ${cname} as one of their admins.\n` +
            `Your super-admin session will be replaced. Sign out and visit /super-admin/login to return.`
        )) return;
        setImpersonating(company.company_id);
        try {
            const { data } = await api.post(`/companies/${company.company_id}/impersonate`);
            // Cookies are set server-side; we only persist the impersonation banner state.
            localStorage.setItem("hrm_impersonation", JSON.stringify({
                by: data.impersonated_by,
                company_id: data.company_id,
                company_name: data.impersonated_company_name,
                started_at: new Date().toISOString(),
            }));
            toast.success(`Signed in as ${data.email} @ ${cname}`);
            window.location.href = "/dashboard";
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Impersonation failed");
            setImpersonating("");
        }
    };

    const onDelete = async (company) => {
        const cid = company.company_id;
        const cname = company.name || cid;
        const typed = window.prompt(
            `PERMANENT DELETE\n\nThis will erase ${cname} and ALL its data (employees, payroll, documents, users). ` +
            `This cannot be undone.\n\nType ${cid} below to confirm:`
        );
        if (!typed) return;
        if (typed.trim().toUpperCase() !== cid) {
            toast.error("Confirmation text did not match the company ID. Aborted.");
            return;
        }
        try {
            const { data } = await api.delete(`/companies/${cid}`, { params: { confirm: cid } });
            const totalCascaded = Object.values(data.cascaded || {}).reduce((a, b) => a + b, 0);
            toast.success(`Deleted ${cid} — ${totalCascaded} records cascaded across ${Object.keys(data.cascaded || {}).length} collections`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="companies-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Platform · Super admin</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02]">
                        Companies
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                        Every tenant on your HRM platform. Each company has fully isolated data, branding, theme and admin users.
                    </p>
                </div>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors rounded-md"
                    data-testid="companies-refresh-btn"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Stats strip */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10 rounded-md overflow-hidden" data-testid="companies-stats">
                <Stat label="Total companies" value={loading ? "—" : items.length} icon={Building2} />
                <Stat label="Combined users" value={loading ? "—" : totalUsers} icon={Users} accent="terracotta" />
                <Stat label="Newest" value={loading ? "—" : (items[0]?.company_id || "—")} icon={Calendar} sub={items[0]?.created_at ? timeAgo(items[0].created_at) : ""} />
            </div>

            {/* Search */}
            <div className="mt-10">
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by Company ID, name or address…"
                        className="w-full pl-10 pr-3 py-2.5 border border-ink/15 text-sm focus:border-klein focus:outline-none bg-white rounded-md"
                        data-testid="companies-search"
                    />
                </div>
            </div>

            {/* Grid of company cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
                {loading ? (
                    <div className="col-span-full text-sm text-ink/40 p-10">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="col-span-full bg-white border border-ink/10 p-10 text-center rounded-md" data-testid="companies-empty">
                        <Building2 className="w-10 h-10 text-ink/20 mx-auto mb-3" strokeWidth={1.5} />
                        <div className="text-sm text-ink/60">{items.length === 0 ? "No companies registered yet." : "No matches for your search."}</div>
                    </div>
                ) : (
                    filtered.map((c) => (
                        <CompanyCard
                            key={c.company_id}
                            company={c}
                            onCopyId={copyId}
                            onToggleStatus={onToggleStatus}
                            onImpersonate={onImpersonate}
                            onDelete={onDelete}
                            impersonating={impersonating === c.company_id}
                            justCopied={copiedId === c.company_id}
                        />
                    ))
                )}
            </div>

            <p className="mt-6 text-xs text-ink/50">
                Tip: companies are created by anyone via the public "Create company" link on the login page.
                Each row has its <code>company_id</code> baked in for full data isolation.
            </p>
        </div>
    );
}

function Stat({ label, value, icon: Icon, accent, sub }) {
    const colorMap = {
        terracotta: "text-terracotta",
        klein: "text-klein",
    };
    return (
        <div className="bg-white p-6">
            <div className="flex items-start justify-between">
                <div className="overline">{label}</div>
                <Icon className={`w-4 h-4 ${colorMap[accent] || "text-ink/30"}`} strokeWidth={1.5} />
            </div>
            <div className={`mt-3 font-display font-black tracking-tighter text-3xl tabular-nums ${colorMap[accent] || "text-ink"} truncate`} title={String(value)}>
                {value}
            </div>
            {sub && <div className="text-xs text-ink/40 mt-1">{sub}</div>}
        </div>
    );
}

function CompanyCard({ company, onCopyId, onToggleStatus, onImpersonate, onDelete, impersonating, justCopied }) {
    const isSuspended = (company.status || "active") === "suspended";
    return (
        <div
            className={`bg-white border rounded-md p-5 shadow-card hover:shadow-lift transition group ${isSuspended ? "border-red-200 bg-red-50/30" : "border-ink/10 hover:border-klein/40"}`}
            data-testid={`company-card-${company.company_id}`}
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    {company.logo_data_url ? (
                        <img src={company.logo_data_url} alt="" className="w-10 h-10 object-cover rounded border border-ink/10 shrink-0" />
                    ) : (
                        <div
                            className="w-10 h-10 rounded font-display font-black text-white text-base flex items-center justify-center shrink-0"
                            style={{ background: company.primary_color || "#105F36" }}
                        >
                            {(company.name || company.company_id || "C")[0].toUpperCase()}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-ink truncate flex items-center gap-2" title={company.name}>
                            {company.name || "(no name)"}
                            {isSuspended && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 border border-red-300 text-red-700 text-[9px] uppercase tracking-[0.15em] rounded shrink-0">
                                    <ShieldAlert className="w-2.5 h-2.5" /> Suspended
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-ink/40 truncate" title={company.address}>{company.address || "—"}</div>
                    </div>
                </div>
            </div>
            <button
                onClick={() => onCopyId(company.company_id)}
                className="inline-flex items-center gap-1.5 mb-4 px-2 py-1 bg-bone border border-ink/10 text-xs font-mono text-ink/80 hover:bg-ink/5 rounded"
                title="Copy Company ID"
                data-testid={`company-copy-${company.company_id}`}
            >
                {justCopied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3 text-ink/40" />}
                <span>{company.company_id}</span>
            </button>

            <div className="grid grid-cols-2 gap-2 text-xs border-t border-ink/5 pt-3">
                <div>
                    <div className="overline mb-1">Users</div>
                    <div className="font-medium text-ink tabular-nums flex items-center gap-1">
                        <Users className="w-3 h-3 text-ink/40" /> {company.user_count || 0}
                    </div>
                </div>
                <div>
                    <div className="overline mb-1">Employees</div>
                    <div className="font-medium text-ink tabular-nums flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-ink/40" /> {company.employee_count || 0}
                    </div>
                </div>
                <div className="col-span-2">
                    <div className="overline mb-1">Created</div>
                    <div className="text-ink/70" title={company.created_at}>
                        {timeAgo(company.created_at)}
                    </div>
                </div>
            </div>

            <div className="mt-4 pt-3 border-t border-ink/5 space-y-2">
                <button
                    onClick={() => onImpersonate(company)}
                    disabled={isSuspended || impersonating}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] font-medium bg-ink text-white hover:bg-klein disabled:opacity-40 disabled:cursor-not-allowed rounded transition"
                    data-testid={`company-impersonate-${company.company_id}`}
                    title={isSuspended ? "Reactivate the company before impersonating" : `Sign in as a ${company.name || company.company_id} admin`}
                >
                    <LogIn className="w-3 h-3" /> {impersonating ? "Signing in…" : "Impersonate admin"}
                </button>
                {company.company_id !== "DEFAULT" && (
                    <>
                        <button
                            onClick={() => onToggleStatus(company)}
                            className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] font-medium border rounded transition ${isSuspended ? "border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white" : "border-amber-300 text-amber-700 hover:bg-amber-600 hover:text-white"}`}
                            data-testid={`company-toggle-${company.company_id}`}
                        >
                            {isSuspended ? <><Play className="w-3 h-3" /> Reactivate</> : <><Pause className="w-3 h-3" /> Suspend</>}
                        </button>
                        {isSuspended && company.status_reason && (
                            <p className="text-[10px] text-red-700/80 italic">"{company.status_reason}"</p>
                        )}
                        <button
                            onClick={() => onDelete(company)}
                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] font-medium border border-red-300 text-red-700 hover:bg-red-700 hover:text-white rounded transition"
                            data-testid={`company-delete-${company.company_id}`}
                            title="Permanently delete this tenant and all its data"
                        >
                            <Trash2 className="w-3 h-3" /> Delete tenant
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
